import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useSite } from '../context/SiteContext';
import { Button } from './ui/button';
import { Factory, BarChart3, Wrench, TrendingUp, LogOut, Menu, X, Zap, MapPin, Activity, Bell, Globe, Package, Languages, DollarSign, ChevronDown, Settings, Building2, FileSpreadsheet, ArrowRightLeft, Server, Ruler, ClipboardCheck, Droplets, GitFork, KeyRound } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import ChangePassword from './ChangePassword';
import { useIndustryConfig } from '../hooks/useIndustryConfig';
import {
  ALL_OPERATIONAL,
  DIRECTION_MENU_ROLES,
  PRODUCTION_DASHBOARD_ROLES,
  PRODUCTION_METRICS_ROLES,
  MAINTENANCE_DASHBOARD_ROLES,
  EXPORT_ROLES,
  DATACENTER_MENU_ROLES,
  BUILDING_MENU_ROLES,
  TONNAGE_MENU_ROLES,
  ZONES_MENU_ROLES,
} from '../utils/roles';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { currency, setCurrency, currencies, language, setLanguage, languages, t } = usePreferences();
  const { selectedSiteId, selectedSiteName, availableSites, selectSite, canChangeSite, canSelectAll, allOptionLabel } = useSite();
  const { industry, isDashboardVisible, getRoleLabel: getRoleLabelByIndustry } = useIndustryConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Ajout : chaque utilisateur peut changer lui-meme son mot de passe.
  const [mdpOuvert, setMdpOuvert] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [exportPerms, setExportPerms] = useState(null);
  const [devicesHealth, setDevicesHealth] = useState(null);
  // Compensation réactive : on n'affiche les dashboards Avant/Après et Amont/Aval
  // que si au moins 1 ligne du site a has_compensation=true.
  // siteHasCompensation === null  = pas encore résolu (afficher par défaut)
  // siteHasCompensation === true  = afficher les 2 dashboards
  // siteHasCompensation === false = masquer les 2 dashboards
  const [siteHasCompensation, setSiteHasCompensation] = useState(null);

  useEffect(() => {
    if (!user?.role) return;
    let cancelled = false;
    api.request('/api/export/permissions')
      .then(perms => {
        if (!cancelled) setExportPerms({
          allowed_roles: perms?.allowed_roles || [],
          allowed_user_ids: perms?.allowed_user_ids || [],
        });
      })
      .catch(() => { if (!cancelled) setExportPerms({ allowed_roles: [], allowed_user_ids: [] }); });
    return () => { cancelled = true; };
  }, [user?.role]);

  useEffect(() => {
    if (!user?.role) return;
    let cancelled = false;
    const fetchHealth = () => {
      const q = selectedSiteId ? `?site_id=${encodeURIComponent(selectedSiteId)}` : '';
      api.request(`/api/realtime/devices/health${q}`)
        .then(h => { if (!cancelled) setDevicesHealth(h); })
        .catch(() => { if (!cancelled) setDevicesHealth({ total: 0, online: 0, status: 'no_devices' }); });
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);  // refresh 30s
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role, selectedSiteId]);

  // Charger la config des lignes pour savoir si le site a une compensation réactive.
  // Détermine si on affiche les dashboards 'Avant/Après' (comparison) et
  // 'Mesures Amont/Aval' (breakers) dans la sidebar.
  useEffect(() => {
    if (!user?.role || !selectedSiteId || selectedSiteId === '_all_') {
      setSiteHasCompensation(null); // pas résolu → affichage par défaut
      return;
    }
    let cancelled = false;
    api.request(`/api/settings/lines?site_id=${encodeURIComponent(selectedSiteId)}`)
      .then(data => {
        if (cancelled) return;
        const lines = (data && Array.isArray(data.lines)) ? data.lines : [];
        const hasComp = lines.some(l => l.has_compensation === true);
        setSiteHasCompensation(hasComp);
      })
      .catch(() => { if (!cancelled) setSiteHasCompensation(null); });
    return () => { cancelled = true; };
  }, [user?.role, selectedSiteId]);

  // Mapping path → clé d'industry (utilisée par useIndustryConfig.dashboards)
  // pour décider si l'item doit s'afficher selon le secteur du client.
  // Les listes de rôles sont centralisées dans src/utils/roles.js
  // (OPS_ROLES, FACILITY_ROLES, ALL_OPERATIONAL, DIRECTION_ROLES, …)

  const menuItems = [
    // Manufacturing
    { path: '/dashboard/production', icon: BarChart3, label: t('production'), roles: PRODUCTION_DASHBOARD_ROLES, industryKey: 'production' },
    { path: '/dashboard/chef-production', icon: ClipboardCheck, label: 'Validations Tonnage', roles: TONNAGE_MENU_ROLES, industryKey: 'chef-production' },
    { path: '/dashboard/maintenance', icon: Wrench, label: t('maintenance'), roles: MAINTENANCE_DASHBOARD_ROLES, industryKey: 'maintenance' },
    // Datacenter
    { path: '/dashboard/dc-exploitation', icon: Server, label: 'Exploitation (24/7)', roles: DATACENTER_MENU_ROLES, industryKey: 'dc-exploitation' },
    { path: '/dashboard/dc-facility', icon: Wrench, label: 'Facility / Cooling', roles: DATACENTER_MENU_ROLES, industryKey: 'dc-facility' },
    { path: '/dashboard/dc-redundancy', icon: GitFork, label: 'Redondance 2N', roles: DATACENTER_MENU_ROLES, industryKey: 'dc-redundancy' },
    // Office / Bureaux
    { path: '/dashboard/building-manager', icon: Building2, label: 'Gestion bâtiment', roles: BUILDING_MENU_ROLES, industryKey: 'building-manager' },
    { path: '/dashboard/building-technician', icon: Wrench, label: 'Maintenance technique', roles: BUILDING_MENU_ROLES, industryKey: 'building-technician' },
    // water_production — SODECI : stations de pompage (Phase 1 : monitoring pompes/moteurs)
    { path: '/dashboard/pumping-station', icon: Droplets, label: 'Station de pompage', roles: PRODUCTION_DASHBOARD_ROLES, industryKey: 'pumping-station' },
    // Transverses
    // Direction : DIRECTION_MENU_ROLES = direction + direction_centrale + energy_manager
    // (composition dédiée sidebar — ne pas élargir DIRECTION_ROLES, utilisée pour des privilèges).
    { path: '/dashboard/direction', icon: TrendingUp, label: t('direction'), roles: DIRECTION_MENU_ROLES, industryKey: 'direction' },
    { path: '/dashboard/zones', icon: MapPin, label: t('zones'), roles: ZONES_MENU_ROLES, industryKey: 'zones' },
    { path: '/dashboard/comparison', icon: Activity, label: t('comparison'), roles: ALL_OPERATIONAL, industryKey: 'comparison' },
    { path: '/dashboard/breakers', icon: ArrowRightLeft, label: industry === 'water_production' ? 'Mesures' : 'Mesures Amont/Aval', roles: ALL_OPERATIONAL, industryKey: 'breakers' },
    { path: '/dashboard/alerts', icon: Bell, label: t('alerts'), roles: ALL_OPERATIONAL, industryKey: 'alerts' },
    { path: '/dashboard/production-metrics', icon: Package, label: t('productionMetrics'), roles: PRODUCTION_METRICS_ROLES, industryKey: 'production-metrics' },
    // multi-site : direction_centrale UNIQUEMENT (direction simple = 1 seul site, pas de vue agrégée)
    { path: '/dashboard/multi-site', icon: Globe, label: t('multiSite'), roles: ['direction_centrale'], industryKey: 'multi-site' },
    // Vues détaillées multi-industry (sidebar : métier seulement, pas d'admin)
    { path: '/dashboard/facility', icon: Server, label: 'Facility (PUE détaillé)', roles: DATACENTER_MENU_ROLES, industryKey: 'facility' },
    { path: '/dashboard/energy-density', icon: Ruler, label: 'kWh/m² (détaillé)', roles: BUILDING_MENU_ROLES, industryKey: 'energy-density' },
    { path: '/dashboard/export', icon: FileSpreadsheet, label: 'Export', roles: EXPORT_ROLES, industryKey: null },
  ];

  const isExportAllowed = () => {
    if (user?.role === 'super_admin') return true;
    if (exportPerms === null) return false;
    if (exportPerms.allowed_roles.includes(user?.role)) return true;
    if (user?.id && exportPerms.allowed_user_ids.includes(user.id)) return true;
    return false;
  };

  // Whitelist par-user : si user.enabled_dashboards est une liste (meme vide),
  // on n'affiche que les items dont industryKey y figure. Champ absent / null
  // = comportement legacy (filtre uniquement par role + industry du client).
  // Export (industryKey === null) reste gere par sa propre logique isExportAllowed().
  const userDashboardWhitelist = Array.isArray(user?.enabled_dashboards) ? user.enabled_dashboards : null;

  const filteredMenu = user?.role === 'admin_client'
    ? menuItems.filter(item => item.path === '/dashboard/export' && isExportAllowed())
    : menuItems
      .filter(item => item.roles.includes(user?.role))
      .filter(item => {
        if (item.path === '/dashboard/multi-site' && user?.site_scope === 'single') return false;
        if (item.path === '/dashboard/export') return isExportAllowed();
        // Filtrage multi-industry : si industryKey défini, doit être dans la liste de l'industry courant
        if (item.industryKey && !isDashboardVisible(item.industryKey)) return false;
        // Compensation réactive : 'Avant/Après' (comparison) visible uniquement
        // si au moins 1 ligne a has_compensation=true.
        // 'Mesures Amont/Aval par disjoncteur' (breakers) reste TOUJOURS visible :
        // utile même sans compensation pour voir les mesures de chaque disjoncteur.
        // (siteHasCompensation === null = pas encore résolu, affichage par défaut)
        if (item.path === '/dashboard/comparison' && siteHasCompensation === false) return false;
        // Whitelist par-user (super_admin a defini une selection dans Admin Client)
        if (userDashboardWhitelist && item.industryKey && !userDashboardWhitelist.includes(item.industryKey)) return false;
        return true;
      });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSiteChange = (siteId) => {
    selectSite(siteId);
    setShowSiteSelector(false);
    // Si on sélectionne un site depuis multi-site, on peut rester sur la page
  };

  // Préfère le label industry-aware (Production / Exploitation / Gestion bâtiment selon le profil).
  const getRoleLabel = (role) => {
    if (role === 'super_admin') return 'Super Admin';
    if (role === 'admin') return 'Administrateur';
    return getRoleLabelByIndustry(role) || role;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div className="flex items-center gap-3">
              <img
                src="/electra-icon.png"
                alt="ELECTRA"
                className="h-10 object-contain rounded-xl"
              />
              <div>
                <h1 className="text-white font-semibold text-sm">ELECTRA</h1>
                <p className="text-slate-400 text-xs">Monitoring Énergétique</p>
              </div>
            </div>

            {/* Nom du site / Sélecteur de site */}
            <div className="hidden md:flex items-center ml-4">
              {canChangeSite ? (
                /* Sélecteur de site pour direction_centrale / multi */
                <div className="relative">
                  <button
                    onClick={() => setShowSiteSelector(!showSiteSelector)}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <span className="text-white text-sm font-medium max-w-[200px] truncate">
                      {selectedSiteName || allOptionLabel || 'Tous les sites'}
                    </span>
                    <ChevronDown className="w-3 h-3 text-slate-400" />
                  </button>

                  {showSiteSelector && (
                    <div className="absolute left-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 min-w-[260px] flex flex-col max-h-[70vh]">
                      {/* Option « Tous » figée en haut, hors zone scrollable */}
                      {canSelectAll && (
                        <div className="border-b border-slate-700 py-1 flex-shrink-0">
                          <button
                            onClick={() => handleSiteChange(null)}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${!selectedSiteId ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                              }`}
                          >
                            <Globe className="w-4 h-4" />
                            {allOptionLabel || 'Tous les sites'}
                          </button>
                        </div>
                      )}
                      {/* Liste scrollable */}
                      <div className="overflow-y-auto py-1 overscroll-contain">
                        {availableSites.map((site) => (
                          <button
                            key={site.site_id || site.id}
                            onClick={() => handleSiteChange(site.site_id || site.id)}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${selectedSiteId === (site.site_id || site.id) ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                              }`}
                          >
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium truncate">{site.name}</p>
                              <p className="text-xs opacity-70 truncate">{site.country}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Badge fixe avec le nom du site pour les utilisateurs mono-site */
                <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-1.5">
                  <MapPin className="w-4 h-4 text-green-400" />
                  <span className="text-white text-sm font-medium">
                    {selectedSiteName || user?.site_id || 'n/d'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Sélecteur de préférences */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreferences(!showPreferences)}
                className="text-slate-400 hover:text-white flex items-center gap-2"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden md:inline">{languages[language].flag} {currencies[currency].symbol}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>

              {showPreferences && (
                <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-lg p-4 shadow-xl z-50 min-w-[200px]">
                  <p className="text-xs text-slate-400 uppercase mb-2">{t('language')}</p>
                  <div className="space-y-1 mb-4">
                    {Object.entries(languages).map(([code, { name, flag }]) => (
                      <button
                        key={code}
                        onClick={() => setLanguage(code)}
                        className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${language === code ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                          }`}
                      >
                        <span>{flag}</span>
                        <span>{name}</span>
                      </button>
                    ))}
                  </div>

                  <p className="text-xs text-slate-400 uppercase mb-2">{t('currency')}</p>
                  <div className="space-y-1">
                    {Object.entries(currencies).map(([code, { name, symbol }]) => (
                      <button
                        key={code}
                        onClick={() => setCurrency(code)}
                        className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${currency === code ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                          }`}
                      >
                        <span>{name}</span>
                        <span className="font-mono">{symbol}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium">{user?.name}</p>
              <p className="text-slate-400 text-xs">{getRoleLabel(user?.role)}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMdpOuvert(true)}
              title="Changer mon mot de passe"
              className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              <KeyRound className="w-4 h-4" />
              <span className="sr-only">Changer mon mot de passe</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      <ChangePassword open={mdpOuvert} onClose={() => setMdpOuvert(false)} />

      <div className="flex">
        {/* Sidebar */}
        {sidebarOpen && (
          <aside className="w-64 bg-slate-900 border-r border-slate-800 min-h-[calc(100vh-4rem)] sticky top-16">
            <nav className="p-4 space-y-2">
              {filteredMenu.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'secondary' : 'ghost'}
                    title={item.label}
                    className={`w-full justify-start overflow-hidden ${isActive
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                    onClick={() => navigate(item.path)}
                  >
                    <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Button>
                );
              })}
            </nav>

            {/* Site actuel dans la sidebar (mobile) */}
            <div className="p-4 md:hidden">
              <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <span className="text-white text-sm font-medium">{selectedSiteName || 'Tous les sites'}</span>
                </div>
              </div>
            </div>

            <div className="p-4 mt-4">
              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  <span className="text-xs font-semibold text-slate-300">{t('systemStatus')}</span>
                </div>
                <div className="space-y-2 text-xs">
                  {(() => {
                    if (!devicesHealth) {
                      return <div className="text-slate-500 italic">Chargement…</div>;
                    }
                    if (devicesHealth.status === 'no_devices') {
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-400">{t('activeSensors')}</span>
                            <span className="text-slate-500 font-medium">aucun</span>
                          </div>
                          <div className="text-slate-500 italic text-[10px]">
                            Aucun compteur enrôlé pour ce client.
                          </div>
                        </>
                      );
                    }
                    const { total, online, physical_devices, status } = devicesHealth;
                    const sensorColor =
                      status === 'stable' ? 'text-green-500' :
                      status === 'degraded' ? 'text-amber-400' :
                      status === 'critical' ? 'text-orange-500' :
                      'text-red-500';
                    const statusLabel =
                      status === 'stable' ? t('stable') :
                      status === 'degraded' ? 'Dégradée' :
                      status === 'critical' ? 'Critique' :
                      status === 'down' ? 'Hors ligne' :
                      'n/d';
                    const statusColor =
                      status === 'stable' ? 'text-green-500' :
                      status === 'degraded' ? 'text-amber-400' :
                      'text-red-500';
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Lignes actives</span>
                          <span className={`font-medium ${sensorColor}`}>{online}/{total}</span>
                        </div>
                        {physical_devices > 0 && physical_devices !== total && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Compteurs physiques</span>
                            <span className="text-slate-300 font-medium">{physical_devices}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-slate-400">{t('connection')}</span>
                          <span className={`font-medium ${statusColor}`}>{statusLabel}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </aside>
        )}

        {/* Main Content */}
        <main className={`flex-1 p-6 ${sidebarOpen ? 'ml-0' : 'ml-0'}`}>
          {children}
        </main>
      </div>

      {/* Overlay pour fermer les dropdowns */}
      {(showSiteSelector || showPreferences) && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => { setShowSiteSelector(false); setShowPreferences(false); }}
        />
      )}
    </div>
  );
};

export default Layout;
